import type { OptionsDocument, QuoteTag } from 'paintscout'
import {
  makeStyles,
  Card,
  Grid,
  useIsMobile,
  ActionButton,
  BulkActionBar,
  BulkActionButton,
  ConfirmationDialog,
  DialogStackContext,
  ResultsTable,
  TableCell,
  TableRow,
  useClientOptions,
  useUser,
  useUserPermissions,
  Link,
  Typography,
  Checkbox,
  EstimatorAvatar,
  useUsers,
  ResultsTableColumnSelect
} from '@paintscout/ui'
import type {
  StyleClasses,
  Auth0UserWithClaims,
  SelectableColumn,
  TableCellProps,
  MuiTheme as Theme
} from '@paintscout/ui'
import type { Deal, User } from '@paintscout/api'

import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import UnarchiveIcon from '@material-ui/icons/Unarchive'
import ArchiveIcon from '@material-ui/icons/Archive'

import { formatCurrency, getQuoteTags, getQuoteTypeOptions, getStageLabel, isUserOwner } from '@paintscout/util/builder'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDealMutations } from 'shared/hooks'
import moment from 'moment'
import classnames from 'classnames'
import { addAuth0Prefix, getActivityColor } from '@paintscout/util'
import isEmpty from 'lodash/isEmpty'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { track } from 'shared/util/analytics'
import { QuoteTagIcons, QuoteTagTooltip } from '../QuoteTagIcons'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      overflow: 'hidden'
    },
    card: {
      border: 'none'
    },
    tableRow: {
      verticalAlign: 'top',
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    },
    tableCell: {
      whiteSpace: 'nowrap'
    },
    tableCellHeader: {
      whiteSpace: 'nowrap'
    },
    denseTableRow: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'start',
      paddingTop: theme.spacing(1.5)
    },
    detailsStageContainer: {
      width: '40%'
    },
    dealDocItem: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: theme.spacing(2),
      paddingRight: theme.spacing(1),
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '&:not(:last-child)': {
        borderBottom: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
      }
    },
    actionsContainer: {
      '@media print': {
        display: 'none'
      }
    }
  }),
  { name: 'DealTable' }
)

export interface DealTableProps {
  classes?: StyleClasses<typeof useStyles>
  deals: Array<Partial<Deal>>

  loading?: boolean

  totalRows: number

  rowsPerPage: number

  checkboxes?: boolean

  actions?: {
    edit?: boolean
    delete?: boolean
    archive?: boolean
    unarchive?: boolean
  }

  actionsForRow?: (row: DealRow) => {
    edit?: boolean
    archive?: boolean
    unarchive?: boolean
    delete?: boolean
  }

  disablePagination?: boolean
  skipRefetchQueries?: boolean

  sort?: string
  onRequestSort?: (event: React.MouseEvent<unknown>, property: string, direction: 'asc' | 'desc') => void

  page: number

  pages: number
  dense?: boolean

  onFetchNextPage?: (page: number) => any
  onPageChange?: (page: number) => any
  onEdit?: (deal: Deal) => any
  onArchive?: (ids: string[], unarchive?: boolean) => any

  onDelete?: (ids: string[]) => any
}
export interface DealRow {
  _id: string
  _rev: string
  name: string
  email?: string
  owner: User
  isOwner: boolean
  created: string
  updated: string
  enteredStage: string
  contact: string
  jobAddress: string
  type: string
  value: string
  stage: string
  pipeline: string
  archived?: boolean
  lead_source?: string
  tags?: QuoteTag[]
}

export type DealResult = Partial<Deal>

function DealTable({
  actions = {},
  actionsForRow,
  checkboxes,
  disablePagination,
  skipRefetchQueries,
  loading,
  onFetchNextPage,
  onPageChange,
  deals,
  rowsPerPage,
  onDelete,
  onArchive,
  dense,
  sort,
  onRequestSort,
  ...otherProps
}: DealTableProps) {
  const classes = useStyles(otherProps)
  const history = useHistory()
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const { user, trialExpired } = useUser()
  const { multiPipeline, canDeactivateSettingsItems } = useFlags()
  const { permissionToEditAllDeals } = useUserPermissions()
  const [selectedRows, setSelectedRows] = useState([])
  const { userPictures, users } = useUsers()
  const { allUnarchiveable, allArchiveable } = useMemo(() => {
    return selectedRows.reduce(
      (prev, current) => {
        if (current.archived) {
          return {
            ...prev,
            allArchiveable: false
          }
        } else {
          return {
            ...prev,
            allUnarchiveable: false
          }
        }
      },
      {
        allUnarchiveable: true,
        allArchiveable: true
      }
    )
  }, [selectedRows])

  const dealUsers = users?.reduce(
    (acc, user) => {
      const { __typename, ...rest } = user
      acc[user.user_id] = rest
      return acc
    },
    {} as { [key: string]: User }
  )

  // const isMobile = useIsMobile({ custom: 1080 }) || dense
  const isMobile = useIsMobile() || dense
  const rows = deals
    .map((deal: DealResult) => mapDealToRow(deal, user, options, dealUsers, !!canDeactivateSettingsItems))
    .filter(Boolean)
  const { deleteDeals, archiveDeals } = useDealMutations()

  const localStorageColumns = JSON.parse(localStorage.getItem('dealTableColumns'))
  const defaultColumns: SelectableColumn[] = [
    { name: 'Name', field: 'name', sortable: true, selected: true },
    multiPipeline && { name: 'Pipeline', field: 'pipeline', sortable: true, selected: true },
    { name: 'Stage', field: 'stage', sortable: true, selected: true },
    { name: 'Date Created', field: 'created', sortable: true, selected: false },
    { name: 'Date Updated', field: 'updated', sortable: true, selected: false },
    { name: 'Time in Stage', field: 'enteredStage', sortable: true, selected: false },
    { name: 'Type', field: 'type', sortable: true, selected: true },
    { name: 'Contact', field: 'contact', sortable: true, selected: true },
    { name: 'Owner', field: 'owner', sortable: true, selected: true },
    { name: 'Location', field: 'jobAddress', sortable: true, selected: true, minWidth: 200 },
    { name: 'Value', field: 'value', sortable: true, align: 'right', selected: true },
    { name: 'Lead Source', field: 'lead_source', sortable: true, selected: false }
  ]
    .filter(Boolean)
    .map((column) => ({
      ...(column as SelectableColumn),
      selected: localStorageColumns ? localStorageColumns.includes(column.field) : column.selected
    }))
  const [columns, setColumns] = useState<SelectableColumn[]>(defaultColumns)
  useEffect(() => {
    setSelectedRows([])
  }, [deals])

  const handlePageChange = useCallback(
    (page: number) => {
      const nextPageFirstIndex = (page - 1) * rowsPerPage
      const dataExists = !!deals[nextPageFirstIndex]

      if (onPageChange) {
        onPageChange(page)
      }

      if (!dataExists && onFetchNextPage) {
        onFetchNextPage(page)
      }
    },
    [onPageChange, onFetchNextPage, deals, rowsPerPage]
  )

  const handleRowClick = useCallback(
    (ev: React.MouseEvent<HTMLElement>, deal: DealRow) => {
      history.push(`/deals/edit/${encodeURIComponent(deal._id)}`)
    },
    [deals]
  )

  const handleAction = useCallback(
    (action: string, dealRows: DealRow[]) => {
      const ids = dealRows.map((deal) => deal._id)

      if (action === 'edit') {
        return history.push(`/deals/edit/${encodeURIComponent(dealRows[0]._id)}`)
      } else if (action === 'delete') {
        openDialog(ConfirmationDialog, {
          message: (
            <span>This will permanently delete {dealRows.length === 1 ? dealRows[0].name : 'the selected deals'}.</span>
          ),
          onConfirm: async () => {
            switch (action) {
              case 'delete':
                track('deals_bulk_deleted', { dealIds: ids, count: ids.length })
                await deleteDeals(ids, skipRefetchQueries).catch((error) => {
                  Sentry.captureException(error, { extra: { ids } })
                  enqueueSnackbar('Unable to delete Deal', { variant: 'error' })
                })
                if (onDelete) {
                  onDelete(ids)
                }
                break
            }
            setSelectedRows([])
            dismissDialog()
          },
          onCancel: dismissDialog
        })
      } else if (action === 'archive' || action === 'unarchive') {
        // Handle archive action
        const unarchive = action === 'unarchive'
        openDialog(ConfirmationDialog, {
          message: (
            <span>
              This will {action} {dealRows.length === 1 ? dealRows[0].name : 'the selected deals'}.
            </span>
          ),
          onConfirm: async () => {
            track(unarchive ? 'deals_bulk_unarchived' : 'deals_bulk_archived', { dealIds: ids, count: ids.length })
            await archiveDeals(ids, { unarchive, skipRefetchQueries }).catch((error) => {
              Sentry.captureException(error)
              enqueueSnackbar(`Unable to ${unarchive ? 'unarchive' : 'archive'}`, { variant: 'error' })
            })

            if (onArchive) {
              onArchive(ids, unarchive)
            }

            setSelectedRows([])
            dismissDialog()
          },
          onCancel: dismissDialog
        })
      }
    },
    [handleRowClick, onDelete]
  )

  function getDealTableColumn({ key, row }: { key: keyof DealRow; row: DealRow }) {
    switch (key) {
      case 'owner':
        return (row[key] as User)?.name
      case 'name':
        return (
          <>
            {row[key] ? row[key].toString() : ''}
            {!isEmpty(row.tags) && (
              <div>
                <QuoteTagTooltip tags={row.tags} tooltipProps={{ placement: 'right' }}>
                  <QuoteTagIcons tags={row.tags} />
                </QuoteTagTooltip>
              </div>
            )}
          </>
        )
      default:
        return row[key] ? row[key].toString() : ''
    }
  }

  const rowActions = useCallback(
    (row: DealRow) => {
      const allowedActions = []
      const currentRowActions = actionsForRow ? actionsForRow(row) : actions
      if (currentRowActions.edit && (row.isOwner || permissionToEditAllDeals)) {
        allowedActions.push({
          key: 'edit',
          label: 'Edit',
          icon: EditIcon
        })
      }

      if (currentRowActions.delete && (row.isOwner || permissionToEditAllDeals)) {
        allowedActions.push({
          key: 'delete',
          label: 'Delete',
          icon: DeleteIcon
        })
      }

      if (currentRowActions.archive && !row.archived && (row.isOwner || permissionToEditAllDeals)) {
        allowedActions.push({
          key: 'archive',
          label: 'Archive',
          icon: ArchiveIcon
        })
      }

      if (currentRowActions.unarchive && row.archived && (row.isOwner || permissionToEditAllDeals)) {
        allowedActions.push({
          key: 'unarchive',
          label: 'Unarchive',
          icon: UnarchiveIcon
        })
      }

      return allowedActions
    },
    [actions, actionsForRow, permissionToEditAllDeals]
  )
  const handleColumnsChange = (selected: string[]) => {
    localStorage.setItem('dealTableColumns', JSON.stringify(selected))
    setColumns(
      defaultColumns.map((column) => ({
        ...column,
        selected: selected.includes(column.field)
      }))
    )
  }

  const createSortHandler = (property: string) => (event: React.MouseEvent) => {
    if (onRequestSort) {
      // get current sort direction
      const currentSort = getSortDirection(property) || 'asc'

      // flip the sort direction
      const newSort = currentSort === 'asc' ? 'desc' : 'asc'

      onRequestSort(event, property, newSort)
    }
  }

  const getSortDirection = (property: string) => {
    const currentSortDirection = sort && sort.startsWith('-') ? 'desc' : 'asc'
    const currentSortName = sort && sort.startsWith('-') ? sort.slice(1) : sort

    if (currentSortName === property) {
      return currentSortDirection
    }
  }

  const renderRow = useCallback(
    (row: DealRow) => {
      if (isMobile) {
        const { stage, name, type, value, owner } = row
        const subtitle = [stage, type, value].filter(Boolean).join(' · ')
        return (
          <TableRow key={row._id} data-testid={`quote-${row._id}`} onClick={(ev) => handleRowClick(ev, row)}>
            <Grid container className={classes.dealDocItem}>
              <Grid item xs>
                <Typography>{name}</Typography>
                <Typography variant={'subtitle2'}>{subtitle}</Typography>
                {!dense && !isEmpty(row.tags) && (
                  <div style={{ marginTop: 2 }}>
                    <QuoteTagIcons tags={row.tags} />
                  </div>
                )}
              </Grid>
              <Grid item style={{ paddingRight: '8px' }}>
                {owner && (
                  <EstimatorAvatar
                    userPictures={userPictures}
                    owner={{
                      id: owner?.user_id,
                      firstName: owner?.user_metadata?.firstName,
                      lastName: owner?.user_metadata?.lastName
                    }}
                    width="36px"
                    height="36px"
                    fontSize="0.9rem"
                    avatarColor={getActivityColor(owner?.user_metadata?.color)}
                  />
                )}
              </Grid>
              {rowActions.length > 0 && (
                <Grid item style={{ marginLeft: '8px' }} className={classes.actionsContainer}>
                  <ActionButton
                    data-testid="actions"
                    actions={rowActions(row)}
                    disabled={rowActions(row).length === 0 || trialExpired}
                    onActionClick={(ev, action) => handleAction(action, [row])}
                  />
                </Grid>
              )}
            </Grid>
          </TableRow>
        )
      } else {
        return (
          <TableRow key={row._id} data-testid={`deal-${row._id}`} className={classes.tableRow}>
            {checkboxes && (
              <TableCell padding="none">
                <Checkbox
                  checked={!!selectedRows.find((r) => r._id === row._id)}
                  onChange={(ev, checked) => {
                    setSelectedRows(checked ? [...selectedRows, row] : selectedRows.filter((r) => r._id !== row._id))
                  }}
                />
              </TableCell>
            )}
            {columns
              .filter((column) => !!column.selected)
              .map((column) => {
                return (
                  <TableCellLink key={column.field} align={column.align} row={row} className={classes.tableCell}>
                    {getDealTableColumn({ key: column.field as any, row })}
                  </TableCellLink>
                )
              })}
            {rowActions.length > 0 && (
              <TableCell align="right">
                <ActionButton
                  data-testid="actions"
                  actions={rowActions(row)}
                  disabled={rowActions(row).length === 0 || trialExpired}
                  onActionClick={(ev, action) => handleAction(action, [row])}
                />
              </TableCell>
            )}
          </TableRow>
        )
      }
    },
    [rowActions, selectedRows, isMobile, columns, checkboxes]
  )

  return (
    <>
      <Card className={classnames(classes.card, classes.dealTableCard)} data-testid={'deal-list'}>
        <ResultsTable
          classes={{ root: classes.root }}
          columns={
            !isMobile && (
              <TableRow>
                {checkboxes && <TableCell isControl padding="none" />}
                {columns
                  .filter((column) => !!column.selected)
                  .map((column) => {
                    const sortable = !!column.sortable
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        fadeWhenInactive
                        sortable={sortable}
                        sortDirection={getSortDirection(column.field)}
                        onClick={sortable ? createSortHandler(column.field) : null}
                        style={{ minWidth: column.minWidth || undefined }}
                        className={classes.tableCellHeader}
                      >
                        {column.name}
                      </TableCell>
                    )
                  })}
                {rowActions.length > 0 && (
                  <TableCell align="right">
                    <ResultsTableColumnSelect columns={columns} onConfirm={handleColumnsChange} disabled={loading} />
                  </TableCell>
                )}
              </TableRow>
            )
          }
          renderRow={renderRow}
          rows={rows}
          rowsPerPage={rowsPerPage}
          disablePagination={disablePagination}
          loading={loading}
          noResultsMessage={'No deals found.'}
          {...otherProps}
          // otherProps wrappers
          onPageChange={handlePageChange}
        />
      </Card>
      <BulkActionBar
        itemName={'Deal'}
        pluralItemName={'Deals'}
        selectedItems={selectedRows}
        onDeselectAll={() => setSelectedRows([])}
      >
        {actions.archive && allArchiveable && (
          <BulkActionButton icon={ArchiveIcon} onClick={() => handleAction('archive', selectedRows)}>
            Archive
          </BulkActionButton>
        )}
        {actions.unarchive && allUnarchiveable && (
          <BulkActionButton icon={UnarchiveIcon} onClick={() => handleAction('unarchive', selectedRows)}>
            Unarchive
          </BulkActionButton>
        )}
        {actions.delete && (
          <BulkActionButton disabled={loading} icon={DeleteIcon} onClick={() => handleAction('delete', selectedRows)}>
            Delete
          </BulkActionButton>
        )}
      </BulkActionBar>
    </>
  )
}

function mapDealToRow(
  deal: Partial<Deal>,
  user: Auth0UserWithClaims,
  options: OptionsDocument,
  users: { [key: string]: User },
  canDeactivate?: boolean
): DealRow {
  if (!deal || !deal.stage) {
    return null
  }
  const dateFormat = options.options.dateFormat.momentValue

  const maxCharacters = 30
  const name = deal.name.length > maxCharacters + 3 ? `${deal.name.substring(0, maxCharacters)}...` : deal.name
  const quoteTypes = getQuoteTypeOptions({ options, canDeactivate })
  const dealQuoteType = quoteTypes.find((type) => type?.key === deal?.quoteType)
  const tagOptions = getQuoteTags({ options })
  const tags = deal.tags || []
  const fullTags = tagOptions.filter((tagOption) => tags.includes(tagOption.name))

  return {
    _id: deal._id,
    _rev: deal._rev,
    name,
    value: dealQuoteType ? formatCurrency({ options, value: deal.value || 0 }) : '',
    stage: getStageLabel({ options, pipelineKey: deal.pipeline?.value, stageKey: deal.stage.value }),
    type: dealQuoteType ? dealQuoteType.label : '',
    contact: `${deal?.contact?.firstName ?? ''}${deal?.contact?.lastName ? ` ${deal?.contact?.lastName}` : ''}`,
    jobAddress: Object.keys(deal?.jobAddress ?? {}).length
      ? `${deal?.jobAddress?.address ?? ''}${deal?.jobAddress?.city ? `, ${deal?.jobAddress?.city}` : ''}`
      : '',
    isOwner: deal?.owner?.id ? isUserOwner(deal, user.user_id) : null,
    owner: deal?.owner?.id ? users?.[addAuth0Prefix(deal?.owner?.id)] : null,
    pipeline: deal.pipeline?.label,
    created: deal.created ? moment(deal.created).format(dateFormat) : '',
    updated: deal.updated ? moment(deal.updated).format(dateFormat) : '',
    enteredStage: deal.enteredStage ? moment(deal.enteredStage).fromNow(true) : '',
    archived: deal.archived || false,
    lead_source: deal.lead_source || '',
    tags: fullTags
  }
}

/**
 * Renders a TableCell with a <Link> component to the deal page
 */
function TableCellLink({ row, className, ...other }: { row: DealRow } & TableCellProps) {
  return (
    <TableCell
      component={(cProps: any) => <Link to={{ pathname: `/deals/edit/${encodeURIComponent(row._id)}` }} {...cProps} />}
      className={className}
      {...other}
    />
  )
}

export default DealTable
